import consts from "shared/consts";
import { RefObject } from "react";
import Konva from "konva";
import { ActuallyMovingElements } from "frontend/canvas-designer-new/canvast-stage.types";
import { getSelectableNodes } from "../node-selection-utils";

// Frame background rendering functions
export function moveFrameBackground(movingElements: ActuallyMovingElements, layerRef: RefObject<Konva.Layer>) {
  if (!layerRef.current) return;

  // Update existing frame background nodes instead of creating new ones
  for (const node of movingElements.nodes) {
    if (node.attrs.type === consts.CANVAS_ELEMENTS.FRAME) {
      const frameElement = node.attrs.element;
      if (frameElement.fill && frameElement.visible !== false) {
        // Find the existing frame background node created by React
        const backgroundNode = layerRef.current?.findOne(
          (backgroundNode: Konva.Node) => backgroundNode.attrs.element?.frameId === node.attrs.id
        );

        if (backgroundNode) {
          // Update the position of the existing background node
          backgroundNode.x(node.x());
          backgroundNode.y(node.y());
        }
      }
    }
  }
}

export function highlightDragEnteringFrame(
  movingElements: ActuallyMovingElements,
  layerRef: RefObject<Konva.Layer>,
  overlayRef: RefObject<Konva.Layer>,
  frames: { [key: string]: any[] },
  hiddenFrameIds: string[]
) {
  if (!layerRef.current || !overlayRef.current) return;

  // Early exit if no frames exist
  if (Object.keys(frames).length === 0) return;

  // Get only non-frame elements being dragged (optimization)
  const draggedNodes = movingElements.nodes.filter(
    (node) => !node.attrs.isFrame && !node.attrs.isFrameTitle && node.attrs.isSelectable
  );

  if (draggedNodes.length === 0) return;

  // Get IDs of frames that are being moved - we don't want to highlight these
  const movingFrameIds = new Set(
    movingElements.nodes.filter((node) => node.attrs.isFrame && !node.attrs.isFrameTitle).map((node) => node.attrs.id)
  );

  // Get visible frame nodes, excluding frames that are being moved
  const frameNodes = getSelectableNodes(
    layerRef,
    (node) => node.attrs.isFrame && !hiddenFrameIds.includes(node.attrs.id) && !movingFrameIds.has(node.attrs.id)
  );

  const stage = layerRef.current;

  // Track which frames and elements to highlight
  const targetFrames = new Set<string>();
  const elementsToHighlight = new Set<Konva.Node>();

  for (const node of draggedNodes) {
    const { x, y, width, height } = node.getClientRect({ relativeTo: stage as any });
    const centerRect = {
      x: x + width / 2,
      y: y + height / 2,
      width: 1,
      height: 1,
    };

    // Find intersecting frame using the same logic as calculateElementsAttachingToFrames
    const intersectingFrame = frameNodes
      .filter((frame: any) => Konva.Util.haveIntersection(frame.getClientRect({ relativeTo: stage }), centerRect))
      .reduce((smallestFrame: any | null, frame: any) => {
        if (!smallestFrame) return frame;
        const { x: x1, y: y1, width: width1, height: height1 } = frame.getClientRect({ relativeTo: stage });
        const { x: x2, y: y2, width: width2, height: height2 } = smallestFrame.getClientRect({ relativeTo: stage });
        if (x1 > x2 && x1 + width1 < x2 + width2 && y1 > y2 && y1 + height1 < y2 + height2) {
          return frame;
        }
        return smallestFrame;
      }, null);

    if (intersectingFrame) {
      targetFrames.add(intersectingFrame.attrs.id);
      elementsToHighlight.add(node);
    }
  }

  // Clear existing highlights
  layerRef.current
    .find(".frame-highlight")
    .toArray()
    .forEach((node: any) => node.destroy());

  // Create highlights for target frames
  for (const frameId of targetFrames) {
    const frameNode = layerRef.current.findOne((node: any) => node.attrs.id === frameId && node.attrs.isFrame);
    if (frameNode && frameNode.attrs.element) {
      const element = frameNode.attrs.element;
      const frameRect = {
        x: element.x,
        y: element.y,
        width: element.width * (element.scaleX || 1),
        height: element.height * (element.scaleY || 1),
      };

      const frameHighlight = new Konva.Rect({
        x: frameRect.x,
        y: frameRect.y,
        width: frameRect.width,
        height: frameRect.height,
        stroke: "#0091E5",
        strokeWidth: 1.2,
        strokeScaleEnabled: false,
        fill: undefined,
        listening: false,
        name: "frame-highlight",
        cornerRadius: 4,
      });

      layerRef.current.add(frameHighlight);
    }
  }

  layerRef.current.batchDraw();
}

export function clearFrameHighlights(layerRef: RefObject<Konva.Layer>) {
  if (layerRef.current) {
    layerRef.current
      .find(".frame-highlight")
      .toArray()
      .forEach((node: any) => node.destroy());
  }
}
